import React from 'react';

import { CustomLoggerProvider, useCustomLogger } from '@vp/pdp-ubik-logging';
import { SWAN_STYLE_KEY_MAP } from '@vp/swan';
import { IdentityProvider, useSwanStyleKeys } from '@vp/ubik-context';
import { LocalizationsProvider, NestedStringObject } from '@vp/ubik-localizations-provider';

import { Breadcrumb } from '../infrastructure/clients/server-side/urlMetadataClient';

import { AuthProvider } from './AuthProvider';
import { OutOfStockToast } from './OutOfStockToast';
import { StickyToast } from './StickyToast';

interface BaseRenderProps {
  localizations: NestedStringObject;
  locale: string;
  isSticky: boolean;
  environment?: string;
}

export interface StickyRenderProps extends BaseRenderProps {
  isSticky: true;
}

export interface DefaultRenderProps extends BaseRenderProps {
  isSticky: false;
  tenant: string;
  productKey: string;
  productVersion: number;
  categoryBreadcrumb: Breadcrumb | undefined;
}

export type IRenderProps = StickyRenderProps | DefaultRenderProps;

const isStickyRenderProps = (props: IRenderProps): props is StickyRenderProps => {
  return props.isSticky;
};

export const Fragment: React.FC<IRenderProps> = (props) => {
  useSwanStyleKeys([SWAN_STYLE_KEY_MAP.core, SWAN_STYLE_KEY_MAP.alertBox]);

  return (
    <CustomLoggerProvider
      context={{
        locale: props.locale,
        fragmentId: '@vp/fragment-pdp-toast',
        fragmentQueryStringParams: {
          global: false,
        },
        environment: props.environment,
      }}
    >
      <WrappedComponent {...props} />
    </CustomLoggerProvider>
  );
};

const WrappedComponent: React.FC<IRenderProps> = (props) => {
  const { logger } = useCustomLogger();

  return (
    <LocalizationsProvider localizations={props.localizations} logger={logger}>
      {isStickyRenderProps(props) ? (
        <StickyToast />
      ) : (
        <IdentityProvider auth={{ culture: props.locale, developmentMode: false }}>
          <AuthProvider>
            <OutOfStockToast
              tenant={props.tenant}
              locale={props.locale}
              productKey={props.productKey}
              productVersion={props.productVersion}
              categoryBreadcrumb={props.categoryBreadcrumb}
            />
          </AuthProvider>
        </IdentityProvider>
      )}
    </LocalizationsProvider>
  );
};
